/**
 * Sync errors service for SubContractorHub (Story SCH-5.2).
 *
 * Wraps three endpoints:
 *   - GET    /sync-errors                       (admin / superadmin)
 *   - PATCH  /sync-errors/{id}/acknowledge      (admin / superadmin)
 *   - PATCH  /sync-errors/{id}/unacknowledge    (admin / superadmin)
 *
 * Mirrors backfillProgressService conventions: fetchJson helper, snake↔camel
 * at the boundary, discriminated-union result, HTTP 403 as a typed result
 * (not a thrown exception).
 */

import { supabase } from '@/lib/supabaseClient';
import type {
  SyncError,
  SyncErrorsErrorObject,
  SyncErrorsFilters,
  SyncErrorsResponse,
  SyncErrorsResult,
} from '@/types/integrations/syncErrors';

const BACKEND_URL =
  import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';

async function authHeader(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

interface ApiEnvelope<TData> {
  data: TData | null;
  error: { code: string; message: string; details?: Record<string, unknown> } | null;
  meta: unknown;
}

async function fetchEnvelope<TData>(
  path: string,
  init: RequestInit,
): Promise<ApiEnvelope<TData> | { _http_403: true }> {
  const headers = await authHeader();
  const response = await fetch(`${BACKEND_URL}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  });
  if (response.status === 403) {
    return { _http_403: true };
  }
  if (!response.ok && response.status !== 200) {
    let detail: string | undefined;
    try {
      const json = await response.json();
      detail = typeof json?.detail === 'string' ? json.detail : undefined;
    } catch {
      // ignore parse error
    }
    throw new Error(detail || `Request failed with status ${response.status}`);
  }
  return (await response.json()) as ApiEnvelope<TData>;
}

const KNOWN_CODES = ['forbidden', 'internal_error', 'not_found'] as const;

function _normalizeError(
  raw: { code: string; message: string; details?: Record<string, unknown> } | null,
): SyncErrorsErrorObject {
  if (!raw) {
    return { code: 'internal_error', message: 'Unknown error' };
  }
  const code = (KNOWN_CODES as readonly string[]).includes(raw.code)
    ? (raw.code as SyncErrorsErrorObject['code'])
    : 'internal_error';
  return { code, message: raw.message, details: raw.details };
}

// ─── Raw → camelCase ──────────────────────────────────────────────────────
interface RawSyncError {
  id: string;
  tenant_id: string;
  tenant_integration_id: string;
  subhub_project_id: string | null;
  error_phase: string;
  error_class_name: string;
  error_signature: string;
  first_message: string;
  count: number;
  first_seen_at: string;
  last_seen_at: string;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  created_at: string;
  updated_at: string;
}

function _mapRow(raw: RawSyncError): SyncError {
  return {
    id: raw.id,
    tenantId: raw.tenant_id,
    tenantIntegrationId: raw.tenant_integration_id,
    subhubProjectId: raw.subhub_project_id ?? null,
    errorPhase: raw.error_phase,
    errorClassName: raw.error_class_name,
    errorSignature: raw.error_signature,
    firstMessage: raw.first_message,
    count: raw.count ?? 0,
    firstSeenAt: raw.first_seen_at,
    lastSeenAt: raw.last_seen_at,
    acknowledgedAt: raw.acknowledged_at ?? null,
    acknowledgedBy: raw.acknowledged_by ?? null,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

interface RawListResponse {
  data: RawSyncError[];
  total_count: number;
  limit: number;
  offset: number;
}

async function listSyncErrors(
  filters: SyncErrorsFilters = {},
): Promise<SyncErrorsResult<SyncErrorsResponse>> {
  const params = new URLSearchParams();
  if (filters.phase) params.set('phase', filters.phase);
  if (filters.subhubProjectId) params.set('subhub_project_id', filters.subhubProjectId);
  if (filters.acknowledged) params.set('acknowledged', filters.acknowledged);
  if (filters.startedAfter) params.set('started_after', filters.startedAfter);
  if (filters.startedBefore) params.set('started_before', filters.startedBefore);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.offset !== undefined) params.set('offset', String(filters.offset));
  const qs = params.toString();
  const path = `/api/v1/integrations/subcontractorhub/sync-errors${qs ? `?${qs}` : ''}`;

  const result = await fetchEnvelope<RawListResponse>(path, { method: 'GET' });
  if ('_http_403' in result) {
    return {
      data: null,
      error: {
        code: 'forbidden',
        message:
          'Administrator access is required to view sync errors.',
      },
    };
  }
  if (result.error) {
    return { data: null, error: _normalizeError(result.error) };
  }
  if (!result.data) {
    return {
      data: null,
      error: { code: 'internal_error', message: 'Empty response from backend' },
    };
  }
  return {
    data: {
      data: (result.data.data ?? []).map(_mapRow),
      totalCount: result.data.total_count ?? 0,
      limit: result.data.limit,
      offset: result.data.offset,
    },
    error: null,
  };
}

async function acknowledgeSyncError(
  errorId: string,
): Promise<SyncErrorsResult<SyncError>> {
  const result = await fetchEnvelope<RawSyncError>(
    `/api/v1/integrations/subcontractorhub/sync-errors/${encodeURIComponent(errorId)}/acknowledge`,
    { method: 'PATCH' },
  );
  if ('_http_403' in result) {
    return { data: null, error: { code: 'forbidden', message: 'Forbidden' } };
  }
  if (result.error) {
    return { data: null, error: _normalizeError(result.error) };
  }
  if (!result.data) {
    return {
      data: null,
      error: { code: 'internal_error', message: 'Empty response from backend' },
    };
  }
  return { data: _mapRow(result.data), error: null };
}

async function unacknowledgeSyncError(
  errorId: string,
): Promise<SyncErrorsResult<SyncError>> {
  const result = await fetchEnvelope<RawSyncError>(
    `/api/v1/integrations/subcontractorhub/sync-errors/${encodeURIComponent(errorId)}/unacknowledge`,
    { method: 'PATCH' },
  );
  if ('_http_403' in result) {
    return { data: null, error: { code: 'forbidden', message: 'Forbidden' } };
  }
  if (result.error) {
    return { data: null, error: _normalizeError(result.error) };
  }
  if (!result.data) {
    return {
      data: null,
      error: { code: 'internal_error', message: 'Empty response from backend' },
    };
  }
  return { data: _mapRow(result.data), error: null };
}

export const syncErrorsService = {
  listSyncErrors,
  acknowledgeSyncError,
  unacknowledgeSyncError,
};
